import { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, Download, Printer } from 'lucide-react';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';

export function LabelHistoryTab({ t: _t }: { t: any }) {
  const { sellerId } = useUIStore();
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [reprinting, setReprinting] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<any>(`/labels/batches?seller_id=${sellerId}&limit=50`);
    if (res.success && res.data) setBatches(res.data);
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const reprint = async (batch: any) => {
    const key = batch.merge_batch_id;
    setReprinting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.post<any>('/labels/merge', {
        seller_id: sellerId,
        shipment_ids: batch.shipment_ids,
        force: true,
      });
      if (res.success && res.data?.url) {
        window.open(res.data.url, '_blank');
        load();
      } else {
        alert((res as any).error || 'No labels to merge');
      }
    } catch (e: any) {
      alert(e.message || 'Error reprinting');
    }
    setReprinting(prev => ({ ...prev, [key]: false }));
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    const date = new Date(d);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">Label History</h2>
        <button onClick={load} className="p-2 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]">
          <RefreshCw size={16} />
        </button>
      </div>
      <p className="text-sm text-[var(--color-text-secondary)]">
        Historial de merge batches. Puedes descargar el PDF o reimprimir (regenerar) un batch anterior.
      </p>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : batches.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">No merge batches yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left">
                <th className="pb-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Fecha</th>
                <th className="pb-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase text-center">Labels</th>
                <th className="pb-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase">Batch ID</th>
                <th className="pb-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b: any) => (
                <tr key={b.merge_batch_id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]">
                  <td className="py-3 text-[var(--color-text)]">{formatDate(b.printed_at)}</td>
                  <td className="py-3 text-center">
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">
                      {b.label_count}
                    </span>
                  </td>
                  <td className="py-3 font-mono text-xs text-[var(--color-text-tertiary)]" title={b.merge_batch_id}>
                    {(b.merge_batch_id || '').slice(0, 20)}
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => b.url ? window.open(b.url, '_blank') : alert('PDF not available')}
                        disabled={!b.url}
                        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                          b.url
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Download size={12} /> Download
                      </button>
                      <button
                        onClick={() => reprint(b)}
                        disabled={reprinting[b.merge_batch_id]}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded text-xs font-medium transition-colors disabled:opacity-50"
                      >
                        {reprinting[b.merge_batch_id]
                          ? <><Loader2 size={12} className="animate-spin" /> Reprinting...</>
                          : <><Printer size={12} /> Reprint</>
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
